import axios from "axios";

/** True when the failure is just "no records yet", not a server/network fault. */
export function isEmptyDataLoadError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 404 || status === 204) return true;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const message = String(data?.message || data?.error || error.message || "").toLowerCase();
  return (
    message.includes("não encontrad") ||
    message.includes("nao encontrad") ||
    message.includes("not found") ||
    message.includes("no data") ||
    message.includes("nenhum") ||
    message.includes("sem registro") ||
    message.includes("lista vazia") ||
    message.includes("empty")
  );
}

/** Pulls list arrays from common API response shapes (including nested `data`). */
export function extractList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  const candidates = [
    obj.itens,
    obj.items,
    obj.data,
    obj.result,
    obj.results,
    obj.records,
    obj.cardapio_itens,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
    return extractList(obj.data);
  }
  return [];
}

/** Loads a list endpoint; returns [] when there is no data yet (no popup upstream). */
export async function fetchListOrEmpty<T>(
  loader: () => Promise<T[]>,
): Promise<T[]> {
  try {
    const result = await loader();
    return Array.isArray(result) ? result : [];
  } catch (error) {
    if (isEmptyDataLoadError(error)) return [];
    throw error;
  }
}

export async function loadAuxiliaryData<T extends unknown[]>(
  loaders: { label: string; load: () => Promise<unknown> }[],
): Promise<T> {
  const results = await Promise.allSettled(loaders.map((l) => l.load()));
  const values: unknown[] = [];

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (result.status === "fulfilled") {
      values.push(result.value);
      continue;
    }
    if (isEmptyDataLoadError(result.reason)) {
      values.push([]);
      continue;
    }
    throw result.reason;
  }

  return values as T;
}
